import React, { PropsWithChildren, useState } from 'react';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Fade, Grid, Modal, Typography } from '@mui/material';
import { EIconTypes } from 'constants/Icons';
import { colors } from 'theme/colors';
import { CustomModalRoot, ModalContent } from './styles';

interface IFormModal extends PropsWithChildren {
  onClickApprove?: () => void;
  onClickClose: () => void;
  open: boolean;
  title?: string;
  text?: string;
  approveButtonLabel?: string;
  approveButtonIcon?: EIconTypes;
  cancelButtonLabel?: string;
  minWidth?: string;
  maxWidth?: string;
  titleLeft?: boolean;
}

export const FormModal = ({
  onClickApprove,
  onClickClose,
  open,
  title,
  text,
  approveButtonLabel,
  approveButtonIcon,
  cancelButtonLabel,
  children,
  minWidth,
  maxWidth,
  titleLeft = false,
}: IFormModal) => {
  const { isDesktop } = useThemeBreakpoint();
  const [isLoading, setIsLoading] = useState(false);

  const handleApprove = async () => {
    if (onClickApprove) {
      setIsLoading(true);
      await onClickApprove();
      setIsLoading(false);
    }
  };

  return (
    <CustomModalRoot style={{ width: open ? '100%' : '0', height: open ? '100%' : '0' }}>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={onClickClose}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={open}>
          <ModalContent
            style={{
              width: isDesktop ? '50%' : '100%',
              minWidth: isDesktop ? minWidth || '780px' : 'auto',
              maxWidth: maxWidth || '780px',
            }}
          >
            {title && (
              <Typography
                variant="h5"
                sx={
                  titleLeft
                    ? { color: colors.primary500, textAlign: 'left', margin: 0 }
                    : { textAlign: 'center' }
                }
              >
                {title}
              </Typography>
            )}
            {text && (
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                }}
              >
                {text}
              </Typography>
            )}
            {children}
            <Grid
              container
              columnSpacing={2}
              size={12}
              sx={{
                rowGap: 2,
                marginTop: '12px',
              }}
            >
              <Grid
                size={{
                  xs: 12,
                  sm: onClickApprove ? 6 : 12,
                }}
              >
                <Button
                  fullWidth
                  label={cancelButtonLabel || 'cancel'}
                  id="cancel"
                  variant={EButtonVariants.outlined}
                  onClick={onClickClose}
                  size={EButtonSizes.small}
                />
              </Grid>
              {onClickApprove && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <Button
                    fullWidth
                    label={approveButtonLabel || 'save'}
                    icon={approveButtonIcon || undefined}
                    id="save"
                    variant={EButtonVariants.contained}
                    onClick={handleApprove}
                    isLoading={isLoading}
                    size={EButtonSizes.small}
                  />
                </Grid>
              )}
            </Grid>
          </ModalContent>
        </Fade>
      </Modal>
    </CustomModalRoot>
  );
};
